import React, { useEffect, useMemo, useRef, useState } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Check, X, Users, Loader2 } from "lucide-react";

/**
 * Searchable multi-select dropdown for WhatsApp groups.
 * Empty selection = "all groups".
 *
 * Props:
 *   value: string[]              -> selected group_chat_ids
 *   onChange: (ids: string[])    -> fired with updated array
 *   label?: string
 *   helpText?: string
 *   testIdPrefix?: string
 */
export default function GroupsMultiSelect({
  value = [],
  onChange,
  label = "Grupos (vacío = todos)",
  helpText = "Si no eliges ninguno, aplica a TODOS los grupos.",
  testIdPrefix = "groups-multi",
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get(`${API}/groups`);
        setGroups(Array.isArray(data) ? data : []);
      } catch { setGroups([]); }
      finally { setLoading(false); }
    })();
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return groups;
    return groups.filter(g =>
      (g.group_name || g.name || g.subject || "").toLowerCase().includes(q) ||
      (g.group_chat_id || "").toLowerCase().includes(q)
    );
  }, [groups, query]);

  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const removeOne = (id, e) => {
    e.stopPropagation();
    onChange((value || []).filter(x => x !== id));
  };

  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  const nameOf = (id) => {
    const g = groups.find(gg => gg.group_chat_id === id);
    return g?.group_name || g?.name || g?.subject || (id || "").slice(0, 20);
  };

  return (
    <div className="space-y-1" ref={wrapRef} data-testid={`${testIdPrefix}-wrap`}>
      {label && <label className="text-xs text-slate-600 font-medium">{label}</label>}

      {/* Selected chips + opener */}
      <div
        className="border rounded-md px-2 py-2 min-h-[42px] cursor-pointer bg-white flex flex-wrap gap-1 items-center hover:border-slate-400"
        onClick={() => setOpen(o => !o)}
        data-testid={`${testIdPrefix}-opener`}
      >
        {(!value || value.length === 0) ? (
          <span className="text-sm text-slate-400 flex items-center gap-1 px-1">
            <Users className="w-3 h-3" /> Todos los grupos
          </span>
        ) : (
          <>
            {value.map(id => (
              <Badge key={id} variant="secondary" className="text-xs pl-2 pr-1 gap-1">
                <span className="max-w-[180px] truncate">{nameOf(id)}</span>
                <button
                  type="button"
                  onClick={(e) => removeOne(id, e)}
                  className="hover:bg-slate-300 rounded-full p-0.5"
                  data-testid={`${testIdPrefix}-remove-${id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-slate-500 hover:text-red-600 ml-auto px-2"
              data-testid={`${testIdPrefix}-clear-all`}
            >
              limpiar
            </button>
          </>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="border rounded-md bg-white shadow-lg max-h-64 overflow-hidden relative z-20" data-testid={`${testIdPrefix}-dropdown`}>
          <div className="p-2 border-b sticky top-0 bg-white">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Buscar grupo..."
                className="pl-8 h-9"
                onClick={e => e.stopPropagation()}
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {loading ? (
              <div className="p-4 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando grupos...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-slate-400 text-sm">Sin resultados.</div>
            ) : (
              filtered.map(g => {
                const id = g.group_chat_id;
                const checked = selectedSet.has(id);
                return (
                  <div
                    key={id}
                    onClick={() => toggle(id)}
                    className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm ${checked ? "bg-emerald-50" : ""}`}
                    data-testid={`${testIdPrefix}-option-${id}`}
                  >
                    <div className={`w-4 h-4 border rounded flex items-center justify-center ${checked ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="flex-1 truncate">{g.group_name || g.name || g.subject || id}</span>
                    {g.is_enabled === false && <span className="text-xs text-slate-400">(off)</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {helpText && <p className="text-xs text-slate-500">{helpText}</p>}
    </div>
  );
}
